import { useCallback } from 'react';
import {
  useAction,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { useWorkflowModel } from './workflow.model';
import { useWorkflowGraphMutations } from './use-workflow-graph-mutations';
import { useOpenAddWorkflowNodeModal } from './use-open-add-workflow-node-modal';
import type { DraftConnectFrom } from './workflow-draft-graph';
import type { WorkflowNodePaletteSelection } from './components/workflow-node-palette';
import { WorkflowToolbarAddTriggerAction } from './workflow-toolbar-add-trigger.action';
import { useArtifactPanel } from '@cotera/client/v0/chat/artifact-panel-context';
import { NewAgentAction } from '../agent/new-agent.action';
import { NewWorkflowAction } from './new-workflow.action';
import {
  resolveConnectFromNodeIds,
  selectionHasWorkflowNode,
} from './workflow-editor-toolbar-state';

export function useWorkflowAddNodeActions(): {
  openAddNodeModal: (connectFrom?: DraftConnectFrom) => void;
} {
  const model = useWorkflowModel();
  const mutations = useWorkflowGraphMutations();
  const { execute: addTrigger } = useAction(WorkflowToolbarAddTriggerAction);
  // "Create new agent" opens the draft agent beside this workflow rather than
  // navigating away from the workflow the user is mid-way through building.
  // Outside a panel there is no beside, and the action navigates as usual.
  const artifactPanel = useArtifactPanel();
  const { execute: createAgent } = useAction(NewAgentAction);
  const openNewAgentArtifact = useCallback(() => {
    void createAgent({
      newChat: false,
      ...(artifactPanel !== null && {
        openArtifact: artifactPanel.openArtifact,
      }),
    });
  }, [artifactPanel, createAgent]);
  // Same beside-not-away treatment for a brand-new workflow to call as a step.
  const { execute: createWorkflow } = useAction(NewWorkflowAction);
  const openNewWorkflowArtifact = useCallback(() => {
    void createWorkflow({
      newChat: false,
      ...(artifactPanel !== null && {
        openArtifact: artifactPanel.openArtifact,
      }),
    });
  }, [artifactPanel, createWorkflow]);
  const selection = useWatchableValue(model.editorSelectionWatchable);

  const handlePaletteSelect = useCallback(
    (
      paletteSelection: WorkflowNodePaletteSelection,
      connectFrom: DraftConnectFrom
    ) => {
      if (paletteSelection.kind === 'agent') {
        void mutations.addAgentNode(
          paletteSelection.agentId,
          paletteSelection.agentName,
          connectFrom
        );
        return;
      }
      if (paletteSelection.kind === 'tool-call') {
        void mutations.addToolCallNode(
          paletteSelection.toolKey,
          paletteSelection.toolName,
          connectFrom
        );
        return;
      }
      if (paletteSelection.kind === 'for-each') {
        void mutations.addForEachNode(connectFrom);
        return;
      }
      if (paletteSelection.kind === 'filter') {
        void mutations.addFilterNode(connectFrom);
        return;
      }
      if (paletteSelection.kind === 'debounce') {
        void mutations.addDebounceNode(connectFrom);
        return;
      }
      if (paletteSelection.kind === 'mapper') {
        void mutations.addMapperNode(connectFrom);
        return;
      }
      if (paletteSelection.kind === 'emit-event') {
        void mutations.addEmitEventNode(connectFrom);
        return;
      }
      if (paletteSelection.kind === 'bucket') {
        void mutations.addBucketNode(connectFrom);
        return;
      }
      if (paletteSelection.kind === 'group-by') {
        void mutations.addGroupByNode(connectFrom);
        return;
      }
      if (paletteSelection.kind === 'run-workflow') {
        void mutations.addRunWorkflowNode(
          paletteSelection.targetWorkflowId,
          paletteSelection.workflowName,
          connectFrom
        );
      }
    },
    [mutations]
  );

  const canAddTrigger = !selectionHasWorkflowNode(selection);

  // Default fan-in source for the toolbar "Add node": every selected non-trigger
  // node. One selected → linear chain; many selected → they all fan into the new
  // node. Explicit overrides (node "+" affordance, connect-drag) still win.
  const connectFromNodeIds = resolveConnectFromNodeIds(selection);

  const openAddNodeModal = useOpenAddWorkflowNodeModal(
    handlePaletteSelect,
    connectFromNodeIds,
    {
      canAddTrigger,
      onAddTrigger: () => void addTrigger(),
      onCreateAgent: openNewAgentArtifact,
      onCreateWorkflow: openNewWorkflowArtifact,
      excludeWorkflowId: model.workflowId,
    }
  );

  return { openAddNodeModal };
}
